/**
 * Curator-only unpublish controls for a PUBLISHED signal, mounted in Settings.
 *
 * The queue cannot host these. It lists withheld signals only, so a published
 * signal never appears there, so the Settings administration picker supplies
 * the published signal context.
 *
 * The modal's own data cannot supply the decision either: v_signal_feed filters
 * on publication_state and publication_version but projects neither, so the
 * detail payload knows the signal is published without knowing which version
 * that is. `get_publication_context` supplies exactly that, and it is requested
 * only when a curator actually initiates an unpublish - never when Settings or
 * an ordinary user's signal detail opens.
 *
 * The version returned is passed through unchanged. It is bigint text, and the
 * one thing that must never happen is the client inventing or reformatting it.
 */

import { useState } from 'react';
import type { DecisionIntent } from '../decision-intent';
import { useCuratorDecision, usePublicationContext } from '../hooks';
import { DECISION_LABELS, type DecisionKind, type PublicationContext } from '../types';
import { CuratorDecisionConfirm } from './CuratorDecisionConfirm';

/** The only two decisions legal from `published`. */
const UNPUBLISH_KINDS: readonly DecisionKind[] = ['unpublish_for_review', 'unpublish_reject'];

export function CuratorPublicationControl({
  signalId,
  onUnpublished,
}: {
  signalId: string;
  /** Called once the signal is no longer published, so Settings can clear it. */
  onUnpublished: () => void;
}): JSX.Element {
  const [choice, setChoice] = useState<DecisionKind | null>(null);
  const [context, setContext] = useState<PublicationContext | null>(null);
  const contextRead = usePublicationContext();
  const decision = useCuratorDecision({
    // The signal has just left the published state, so the Settings selection
    // no longer belongs in the published-signal picker.
    onSuccess: () => {
      setChoice(null);
      setContext(null);
      onUnpublished();
    },
  });

  const open = (kind: DecisionKind) => {
    // Switching action mid-read would leave a context fetched for one decision
    // arriving under another, so the choice is locked until the read settles.
    if (contextRead.isPending || decision.isPending) return;
    if (choice === kind) {
      setChoice(null);
      return;
    }
    setChoice(kind);
    setContext(null);
    decision.reset();
    contextRead.reset();
    // Fetch the authoritative version for THIS attempt, at the moment the
    // curator commits to acting.
    contextRead.mutate(signalId, {
      onSuccess: (fetched) => setContext(fetched),
    });
  };

  const refreshAuthoritativeState = () => {
    if (contextRead.isPending) return;
    setContext(null);
    contextRead.reset();
    contextRead.mutate(signalId, {
      // The conflict is cleared ONLY here, inside the success callback. Clearing
      // beside the call would release the block while the re-read was still in
      // flight - or after it had failed - letting the curator resubmit against
      // the same stale version and collect the same 409. A failed re-read leaves
      // the conflict standing, which is the honest outcome.
      onSuccess: (fetched) => {
        setContext(fetched);
        decision.clearConflict();
      },
    });
  };

  const confirm = (reason: string | null) => {
    if (!choice || !context) return;
    const intent: DecisionIntent = {
      signalId,
      kind: choice,
      reason,
      metadata: {},
      expectedPublicationState: context.publicationState,
      // Verbatim from get_publication_context. Never derived, never guessed.
      expectedPublicationVersion: context.publicationVersion,
    };
    decision.submit(intent);
  };

  return (
    <div className="sx-z-pursuit">
      <p className="sx-zone-title sx-sm">Signal curation</p>
      <p className="sx-helper">This signal is published and visible to users.</p>

      <div className="sx-outreach-list">
        {UNPUBLISH_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className="sx-btn sx-btn-p2"
            aria-haspopup="dialog"
            aria-expanded={choice === kind}
            // Locked while either request runs: switching mid-flight would let a
            // context read for one decision land under a different one.
            disabled={decision.isPending || contextRead.isPending}
            onClick={() => open(kind)}
          >
            {DECISION_LABELS[kind]}
          </button>
        ))}
      </div>

      {choice && contextRead.isPending ? (
        <p className="sx-helper" role="status">
          Reading the current publication state…
        </p>
      ) : null}

      {choice && contextRead.error ? (
        <p className="sx-popover-help sx-popover-help--error" role="alert">
          {contextRead.error.message}
        </p>
      ) : null}

      {/*
        The confirm step appears only once an authoritative version is in hand.
        Without it there is nothing honest to submit, and guessing a version is
        exactly the failure this control exists to prevent.
      */}
      {choice && context ? (
        <CuratorDecisionConfirm
          kind={choice}
          busy={decision.isPending}
          error={decision.error}
          conflicted={decision.isConflicted}
          blockReason={decision.blockReason}
          refreshing={contextRead.isPending}
          onCancel={() => {
            setChoice(null);
            setContext(null);
          }}
          onConfirm={confirm}
          onRefresh={refreshAuthoritativeState}
        />
      ) : null}
    </div>
  );
}
